import React, { useState, useEffect, useRef } from 'react';
import {
  Snackbar,
  Alert,
  Button,
  Box,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';

const SessionTimeoutNotification: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isExtending, setIsExtending] = useState(false);
  const [extendError, setExtendError] = useState<string | null>(null);
  const timeLeftRef = useRef(0);

  useEffect(() => {
    timeLeftRef.current = timeLeft;
  }, [timeLeft]);

  useEffect(() => {
    if (!isAuthenticated) {
      setShowWarning(false);
      setExtendError(null);
      return;
    }

    const checkTokenExpiration = () => {
      const expiresAtRaw = localStorage.getItem('token_expires_at');
      if (!expiresAtRaw) {
        setShowWarning(false);
        return;
      }

      const timeUntilExpiry = new Date(expiresAtRaw).getTime() - Date.now();
      if (timeUntilExpiry > 0 && timeUntilExpiry <= 5 * 60 * 1000) {
        setTimeLeft(Math.ceil(timeUntilExpiry / 1000));
        setShowWarning(true);
      } else if (timeUntilExpiry > 5 * 60 * 1000) {
        setShowWarning(false);
        setExtendError(null);
      }
    };

    checkTokenExpiration();
    const interval = setInterval(checkTokenExpiration, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!showWarning) {
      return;
    }

    const timer = setInterval(() => {
      const next = timeLeftRef.current - 1;
      if (next <= 0) {
        setShowWarning(false);
        setTimeLeft(0);
        return;
      }
      setTimeLeft(next);
    }, 1000);

    return () => clearInterval(timer);
  }, [showWarning]);

  const handleExtendSession = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (isExtending) {
      return;
    }

    setIsExtending(true);
    setExtendError(null);
    try {
      await authAPI.refreshToken();
      setShowWarning(false);
      setTimeLeft(0);
    } catch (error) {
      console.error('Failed to extend session:', error);
      setExtendError('Unable to extend session. Please save your work and sign in again.');
    } finally {
      setIsExtending(false);
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <Snackbar
      open={showWarning}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      ClickAwayListenerProps={{ mouseEvent: false, touchEvent: false }}
    >
      <Alert
        severity="warning"
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              color="inherit"
              size="small"
              onClick={handleExtendSession}
              disabled={isExtending}
              sx={{ color: '#ffffff' }}
            >
              {isExtending ? 'Extending...' : 'Extend Session'}
            </Button>
          </Box>
        }
        sx={{
          backgroundColor: '#f57c00',
          color: '#ffffff',
          '& .MuiAlert-message': {
            color: '#ffffff',
          },
        }}
      >
        {extendError
          ? extendError
          : `Your session will expire in ${formatTime(timeLeft)}. Click "Extend Session" to stay logged in.`}
      </Alert>
    </Snackbar>
  );
};

export default SessionTimeoutNotification;
